import React, { CSSProperties, FC, SVGProps as ReactSVGProps } from 'react';
import styled from 'styled-components';
import { Size } from 'src/types';
import {
  AddCircle,
  AddCircleUnfilled,
  ArrowDown,
  ArrowUp,
  B2CCalendar,
  B2CCard,
  B2CChild,
  B2CDollar,
  B2CLoading,
  B2CPerson,
  Brightness,
  Calendar,
  CheckCircle,
  Chevron,
  ChevronBack,
  ChevronRight,
  Close,
  Cloud,
  Done,
  Drag,
  Email,
  Error,
  ExpandLess,
  ExpandMore,
  Filter,
  Flag,
  Hamburger,
  History,
  Info,
  InfoEmpty,
  Label,
  People,
  Phone,
  Play,
  RemoveCircle,
  RemoveCircleUnfilled,
  Schedule,
  Sort,
  Star,
  Trash,
  VerticalEllipsis,
  Warning,
} from './Icons';

export const IconName = {
  hamburger: 'hamburger',
  chevron: 'chevron',
  chevronBack: 'chevronBack',
  chevronRight: 'chevronRight',
  sort: 'sort',
  arrowUp: 'arrowUp',
  arrowDown: 'arrowDown',
  play: 'play',
  calendar: 'calendar',
  expandMore: 'expandMore',
  expandLess: 'expandLess',
  filter: 'filter',
  people: 'people',
  schedule: 'schedule',
  cloud: 'cloud',
  star: 'star',
  done: 'done',
  flag: 'flag',
  label: 'label',
  brightness: 'brightness',
  addCircle: 'addCircle',
  removeCircle: 'removeCircle',
  addCircleUnfilled: 'addCircleUnfilled',
  removeCircleUnfilled: 'removeCircleUnfilled',
  close: 'close',
  checkCircle: 'checkCircle',
  email: 'email',
  phone: 'phone',
  info: 'info',
  infoEmpty: 'infoEmpty',
  error: 'error',
  warning: 'warning',
  verticalEllipsis: 'verticalEllipsis',
  drag: 'drag',
  trash: 'trash',
  history: 'history',
  b2cLoading: 'b2cLoading',
  b2cPerson: 'b2cPerson',
  b2cChild: 'b2cChild',
  b2cCalendar: 'b2cCalendar',
  b2cCard: 'b2cCard',
  b2cDollar: 'b2cDollar',
} as const;

export type IconNameType = typeof IconName[keyof typeof IconName];

export type Icons = { [key in IconNameType]: JSX.Element };

export const icons: Icons = {
  hamburger: Hamburger,
  chevron: Chevron,
  sort: Sort,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  chevronBack: ChevronBack,
  chevronRight: ChevronRight,
  play: Play,
  calendar: Calendar,
  expandMore: ExpandMore,
  expandLess: ExpandLess,
  filter: Filter,
  people: People,
  schedule: Schedule,
  cloud: Cloud,
  star: Star,
  done: Done,
  flag: Flag,
  label: Label,
  brightness: Brightness,
  addCircle: AddCircle,
  removeCircle: RemoveCircle,
  addCircleUnfilled: AddCircleUnfilled,
  removeCircleUnfilled: RemoveCircleUnfilled,
  close: Close,
  checkCircle: CheckCircle,
  email: Email,
  phone: Phone,
  info: Info,
  infoEmpty: InfoEmpty,
  error: Error,
  warning: Warning,
  verticalEllipsis: VerticalEllipsis,
  drag: Drag,
  trash: Trash,
  history: History,
  b2cLoading: B2CLoading,
  b2cPerson: B2CPerson,
  b2cChild: B2CChild,
  b2cCalendar: B2CCalendar,
  b2cCard: B2CCard,
  b2cDollar: B2CDollar,
};

interface IconProps extends SVGProps {
  name: IconNameType;
  className?: string;
  viewBox?: string;
  wrapperStyle?: CSSProperties;
}

export const Icon: FC<IconProps> = ({
  size = 'medium',
  color,
  className,
  wrapperStyle,
  isInverted,
  name,
  viewBox: customViewBox, // TODO: Remove from props, should be a part of the icon itself and not to be amended
}) => {
  const viewBox = customViewBox || getViewBox(name);

  return (
    <Svg
      className={className}
      style={wrapperStyle}
      viewBox={viewBox}
      iconSize={size}
      iconColor={color || (isInverted ? 'white' : 'black')}
    >
      {icons[name]}
    </Svg>
  );
};

function getViewBox(iconName: string) {
  switch (iconName) {
    case IconName.hamburger:
      return '0 0 40 40';
    case IconName.sort:
      return '0 0 320 512';
    case IconName.arrowUp:
    case IconName.arrowDown:
      return '0 0 448 512';
    case IconName.play:
    case IconName.chevronBack:
      return '0 0 32 32';
    case IconName.expandMore:
    case IconName.expandLess:
    case IconName.phone:
    case IconName.email:
    case IconName.info:
    case IconName.trash:
    case IconName.history:
      return '0 0 24 24';
    case IconName.cloud:
    case IconName.schedule:
    case IconName.star:
    case IconName.done:
    case IconName.filter:
    case IconName.flag:
    case IconName.label:
    case IconName.brightness:
    case IconName.verticalEllipsis:
      return '0 0 25 25';
    case IconName.addCircle:
    case IconName.removeCircle:
    case IconName.addCircleUnfilled:
    case IconName.removeCircleUnfilled:
    case IconName.close:
    case IconName.checkCircle:
    case IconName.error:
    case IconName.warning:
    case IconName.drag:
      return '0 0 24 24';
    case IconName.b2cLoading:
      return '0 0 67 64';
    case IconName.b2cPerson:
    case IconName.infoEmpty:
    case IconName.b2cChild:
      return '0 0 16 16';
    case IconName.b2cCalendar:
      return '0 0 18 21';
    case IconName.b2cCard:
    case IconName.b2cDollar:
      return '0 0 54 54';
    default:
      return '0 0 20 20';
  }
}

type SVGProps = ReactSVGProps<object> & {
  size?: Size;
  iconColor?: string;
  isInverted?: boolean;
  iconSize?: string;
};

const Svg = styled.svg<SVGProps>`
  color: ${({ theme, iconColor }) => theme?.colors?.[iconColor]};
  fill: ${({ theme, iconColor }) => theme?.colors?.[iconColor]};
  width: ${({ theme, iconSize }) => theme?.size?.[iconSize]?.width ?? '1em'};
  height: ${({ theme, iconSize }) => theme?.size?.[iconSize]?.height ?? '1em'};
`;
